import React, { ReactNode } from 'react';

import { CoreUtils } from '@nhl/nhl-breakaway-ui';
import { SortingState } from '@tanstack/react-table';

import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';

import StandingsTable from './components/StandingsTable/StandingsTable';
import { RankAccessor, StandingsViewType, StandingsViewTypeLabel } from './enums';
import StandingsMessages from './messages';
import { OrganizationStandings, StandingsSeason, TeamStandingsRecord } from './types';
import {
    getSortedOrganizations,
    getStandingsByOrganization,
    getWildcardsFromConference,
    teamRecordsComparator,
} from './utils';

import { ConferenceHeader, ConferenceHeaderWrapper, DivisionHeader } from './styles';

type StandingsSectionParams = {
    compareProperty: RankAccessor;
    identifier: number;
    orgName: string;
    orgStandings: Array<TeamStandingsRecord>;
    selectedSeason: StandingsSeason;
    setSortingState: (sortingState: SortingState) => void;
    sortingState: SortingState;
};

const renderConferenceHeader = (organizationId: string, conferenceIndex: number, isWildcard?: boolean): ReactNode => {
    const key: string = (isWildcard ? 'wildcard-conference-' : 'organization-') + organizationId + conferenceIndex;
    return (
        <ConferenceHeaderWrapper key={key}>
            <ConferenceHeader aria-level={2}>
                <CoreUtils.SafeFormattedMessage
                    message={
                        StandingsMessages.orgLabels.conference[organizationId] ||
                        StandingsMessages.orgLabels.conference.default
                    }
                />
            </ConferenceHeader>
        </ConferenceHeaderWrapper>
    );
};

// is responsible for rendering a single standings section
const renderSingleStandingsSection = ({
    compareProperty,
    identifier,
    orgName,
    orgStandings,
    selectedSeason,
    setSortingState,
    sortingState,
}: StandingsSectionParams): ReactNode => {
    const isDisplayingOrgName: boolean =
        compareProperty === RankAccessor.division || compareProperty === RankAccessor.wildcard;

    if (!orgStandings?.length) {
        return null;
    }

    const sortedStandingsData: Array<TeamStandingsRecord> = orgStandings.sort(
        teamRecordsComparator.bind(this, compareProperty)
    );

    return (
        <div key={'org-name-' + orgName + identifier}>
            {isDisplayingOrgName && (
                <DivisionHeader role="heading" aria-level={3}>
                    <CoreUtils.SafeFormattedMessage
                        message={
                            StandingsMessages.orgLabels.division[orgName] ||
                            StandingsMessages.orgLabels.division.default
                        }
                    />
                </DivisionHeader>
            )}
            <StandingsTable
                displayedSeason={selectedSeason}
                rankAccessor={compareProperty}
                data={sortedStandingsData}
                setSortingState={setSortingState}
                sortingState={sortingState}
            />
        </div>
    );
};

// given a conference, it will get all related wildcard standings sections
const getWildcardStandingsSectionsForConference = (
    conferenceId: string,
    conference: { standings: Array<TeamStandingsRecord> },
    selectedSeason: StandingsSeason,
    conferenceIndex: number,
    sortingState: SortingState,
    setSortingState
): Array<ReactNode> => {
    const standingsSection: Array<ReactNode> = [];
    const isInsertingAdvertisement: boolean = conferenceIndex === 1 && window.__AD_SETTINGS__.leaderDivIds.length > 0;
    if (isInsertingAdvertisement) {
        standingsSection.push(
            <AdTag
                key={window.__AD_SETTINGS__.leaderDivIds[conferenceIndex - 1]}
                divId={window.__AD_SETTINGS__.leaderDivIds[conferenceIndex - 1]}
                adType={AdType.LEADER}
                forceReload
            />
        );
    }

    if (selectedSeason.conferencesInUse) {
        standingsSection.push(renderConferenceHeader(conferenceId, conferenceIndex, true));
    }

    // First we determine if the team is one of the divisional leaders for this conference (that is, they
    // are above the wildcards in standings) or is one of the teams in contention for a wildcard spot. This
    // is indicated by having a wildcard sequence value = 0 or greater than 0 respectively (see inside of helper)
    const {
        divisionLeaders,
        wildcards,
    }: { divisionLeaders: Array<TeamStandingsRecord>; wildcards: Array<TeamStandingsRecord> } =
        getWildcardsFromConference(conference);

    // Then we take those division leaders and map them by their division
    const leadersByDivision: OrganizationStandings = getStandingsByOrganization(
        StandingsViewTypeLabel.division,
        divisionLeaders
    );

    // We display the division leaders for each division in their own sections first
    const divisionLeadersSection: Array<ReactNode> = getSortedOrganizations(leadersByDivision).map(
        (divisionId, index) =>
            renderSingleStandingsSection({
                compareProperty: RankAccessor.division,
                identifier: index,
                orgName: divisionId,
                orgStandings: leadersByDivision[divisionId].standings,
                selectedSeason,
                setSortingState,
                sortingState,
            })
    );

    standingsSection.push(divisionLeadersSection);

    // Then we display the rest of the teams in a Wildcard section
    const wildcardSection: ReactNode = renderSingleStandingsSection({
        compareProperty: RankAccessor.wildcard,
        identifier: conferenceIndex,
        orgName: StandingsViewTypeLabel.wildcard,
        orgStandings: wildcards,
        selectedSeason,
        setSortingState,
        sortingState,
    });

    standingsSection.push(wildcardSection);

    // flattening to avoid nested arrays
    return standingsSection.flat(1);
};

// gets all wildcard standings sections for page
const getWildcardStandingsSections = (
    standingsData,
    selectedSeason,
    sortingState,
    setSortingState
): Array<ReactNode> => {
    const organizedRecords: OrganizationStandings = getStandingsByOrganization(
        StandingsViewTypeLabel.conference,
        standingsData
    );
    const standingsSections: Array<ReactNode> = getSortedOrganizations(organizedRecords).map((conferenceId, index) =>
        getWildcardStandingsSectionsForConference(
            conferenceId,
            organizedRecords[conferenceId],
            selectedSeason,
            index,
            sortingState,
            setSortingState
        )
    );

    // flattening to avoid nested arrays
    return standingsSections.flat(1);
};

// gets either a division, full conference, or league-wide view of standings, depending on selected view type
const getStandingsSectionsFromOrganization = (
    organizationId,
    organization,
    standingsViewType,
    selectedSeason,
    conferenceIndex,
    sortingState,
    setSortingState
): Array<ReactNode> => {
    const standingsSection: Array<ReactNode> = [];

    const headerProperty: StandingsViewTypeLabel = StandingsViewTypeLabel[standingsViewType];
    const compareProperty: RankAccessor = RankAccessor[standingsViewType];

    const isDivisionViewWithConferences: boolean =
        standingsViewType === StandingsViewType.division && selectedSeason.conferencesInUse;

    if (isDivisionViewWithConferences) {
        const isInsertingAdvertisement: boolean =
            conferenceIndex === 1 && window.__AD_SETTINGS__.leaderDivIds.length > 0;
        if (isInsertingAdvertisement) {
            standingsSection.push(
                <AdTag
                    key={window.__AD_SETTINGS__.leaderDivIds[conferenceIndex - 1]}
                    divId={window.__AD_SETTINGS__.leaderDivIds[conferenceIndex - 1]}
                    adType={AdType.LEADER}
                    forceReload
                />
            );
        }
        standingsSection.push(renderConferenceHeader(organizationId, conferenceIndex));
        const organizedDivisionalRecords: OrganizationStandings = getStandingsByOrganization(
            headerProperty,
            organization.standings
        );
        const divisionStandingsSections: Array<ReactNode> = getSortedOrganizations(organizedDivisionalRecords).map(
            (divisionId, index) =>
                renderSingleStandingsSection({
                    compareProperty,
                    identifier: index,
                    orgName: divisionId,
                    orgStandings: organizedDivisionalRecords[divisionId].standings,
                    selectedSeason,
                    setSortingState,
                    sortingState,
                })
        );
        standingsSection.push(divisionStandingsSections);
    } else {
        const isInsertingAdvertisement: boolean =
            conferenceIndex === 1 && window.__AD_SETTINGS__.leaderDivIds.length > 0;
        if (standingsViewType === StandingsViewType.conference && isInsertingAdvertisement) {
            standingsSection.push(
                <AdTag
                    key={window.__AD_SETTINGS__.leaderDivIds[conferenceIndex - 1]}
                    divId={window.__AD_SETTINGS__.leaderDivIds[conferenceIndex - 1]}
                    adType={AdType.LEADER}
                    forceReload
                />
            );
        }

        if (standingsViewType === StandingsViewType.league || selectedSeason.conferencesInUse) {
            standingsSection.push(renderConferenceHeader(organizationId, conferenceIndex));
        }

        const section: ReactNode = renderSingleStandingsSection({
            compareProperty,
            identifier: conferenceIndex,
            orgName: organizationId,
            orgStandings: organization.standings,
            selectedSeason,
            setSortingState,
            sortingState,
        });

        standingsSection.push(section);
    }

    // flattening to avoid nested arrays
    return standingsSection.flat(1);
};

// gets all standings sections for page
export const getStandingsSections = (
    standingsViewType: StandingsViewType | undefined,
    standingsData: Array<TeamStandingsRecord> | null,
    selectedSeason: StandingsSeason,
    sortingState: SortingState,
    setSortingState: (sortingState: SortingState) => void
): Array<ReactNode> | null => {
    if (!standingsData || !selectedSeason || !standingsViewType) {
        return null;
    }

    if (standingsViewType === StandingsViewType.wildcard) {
        return getWildcardStandingsSections(standingsData || [], selectedSeason, sortingState, setSortingState);
    }

    const headerProperty: StandingsViewTypeLabel = StandingsViewTypeLabel[standingsViewType];

    const isDivisionViewWithConferences: boolean =
        standingsViewType === StandingsViewType.division && selectedSeason.conferencesInUse;

    const organizedRecords: OrganizationStandings = isDivisionViewWithConferences
        ? getStandingsByOrganization(StandingsViewTypeLabel.conference, standingsData || [])
        : getStandingsByOrganization(headerProperty, standingsData || []);

    const standingsSections: Array<ReactNode> = getSortedOrganizations(organizedRecords).map((organizationId, index) =>
        getStandingsSectionsFromOrganization(
            organizationId,
            organizedRecords[organizationId],
            standingsViewType,
            selectedSeason,
            index,
            sortingState,
            setSortingState
        )
    );

    // flattening to avoid nested arrays
    return standingsSections.flat(1);
};
